import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PaintingCanvas from '../components/ui/PaintingCanvas';
import Button from '../components/ui/Button';
import SafeIcon from '../components/common/SafeIcon';
import { MEMBERSHIP_PLANS, SCENE_CATALOG } from '../config/membership';
import { billingEnabled } from '../services/billingService';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiCheck, FiPause, FiPlay, FiRefreshCw } = FiIcons;
const DEMO_DURATION = 30000;
const FREE_SCENES = SCENE_CATALOG.filter((scene) => scene.tier === 'free');
const DEFAULT_SCENE = FREE_SCENES[0];

export default function Welcome() {
  const [scene, setScene] = useState(DEFAULT_SCENE?.id || 'threshold-of-autumn');
  const [demoState, setDemoState] = useState('ready');
  const [progress, setProgress] = useState(0);
  const startedAtRef = useRef(0);
  const elapsedRef = useRef(0);
  const frameRef = useRef(null);

  useEffect(() => {
    if (demoState !== 'running') return undefined;

    startedAtRef.current = Date.now() - elapsedRef.current;

    const animate = () => {
      const elapsed = Math.min(
        DEMO_DURATION,
        Date.now() - startedAtRef.current
      );

      elapsedRef.current = elapsed;
      setProgress(elapsed / DEMO_DURATION);

      if (elapsed < DEMO_DURATION) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        setDemoState('complete');
      }
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [demoState]);

  const startDemo = () => {
    elapsedRef.current = 0;
    setProgress(0);
    setDemoState('running');
  };

  const pauseDemo = () => {
    elapsedRef.current = Math.min(
      DEMO_DURATION,
      Date.now() - startedAtRef.current
    );
    setDemoState('paused');
  };

  const replayDemo = () => {
    elapsedRef.current = 0;
    setProgress(0);
    setDemoState('running');
  };

  const chooseScene = (sceneId) => {
    setScene(sceneId);
    elapsedRef.current = 0;
    setProgress(0);
    setDemoState('ready');
  };

  const plus = MEMBERSHIP_PLANS.plus;

  return (
    <main className="min-h-screen bg-paper text-pine">
      <nav
        aria-label="Welcome navigation"
        className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8"
      >
        <Link to="/welcome" className="font-cormorant text-2xl font-bold">
          MindGarden
        </Link>
        <div className="flex items-center gap-4 text-sm font-bold">
          <a href="#painting-demo" className="hidden hover:text-sage sm:inline">
            Studio
          </a>
          <Link to="/membership" className="hidden hover:text-sage sm:inline">
            Plans
          </Link>
          <Link
            to="/"
            className="rounded-full bg-pine px-4 py-2 text-paper transition hover:bg-pine/90"
          >
            Open app
          </Link>
        </div>
      </nav>

      <section className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-20 pt-10 sm:px-8 lg:grid-cols-[1.05fr_.95fr] lg:pb-28 lg:pt-20">
        <div>
          <p className="mb-5 text-xs font-bold uppercase tracking-[0.22em] text-sage">
            Your effort, made visible.
          </p>
          <h1 className="max-w-3xl font-cormorant text-5xl font-bold leading-[0.98] sm:text-6xl lg:text-7xl">
            Make time. Make something worth keeping.
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-pine/70 sm:text-xl">
            Plan a little. Focus deeply. Turn your effort into a growing collection of art.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button as={Link} to="/" variant="primary" size="lg" icon={FiArrowRight}>
              Start free
            </Button>
            <a
              href="#painting-demo"
              className="inline-flex items-center rounded-xl border border-pine/15 bg-white px-5 py-3 font-bold text-pine transition hover:bg-pine/5 focus:outline-none focus:ring-2 focus:ring-sage"
            >
              Try the painting
            </a>
          </div>
          <p className="mt-4 text-sm text-pine/55">
            No paywall required to plan, focus, or keep your paintings.
          </p>
        </div>

        <div className="relative">
          <div className="absolute -inset-5 rounded-[2.5rem] bg-sage/15 blur-2xl" />
          <div className="relative rounded-[2rem] border border-pine/10 bg-white p-3 shadow-xl">
            <PaintingCanvas
              scene={DEFAULT_SCENE?.id || 'threshold-of-autumn'}
              progress={1}
              artSeed={18472}
              className="aspect-square w-full rounded-[1.4rem] object-contain"
            />
            <div className="flex items-center justify-between px-3 py-3">
              <div>
                <p className="font-bold">{DEFAULT_SCENE?.name || 'Threshold of Autumn'}</p>
                <p className="text-xs text-pine/50">A completed focus painting</p>
              </div>
              <span className="rounded-full bg-sage/15 px-3 py-1 text-xs font-bold">
                Free scene
              </span>
            </div>
          </div>
        </div>
      </section>

      <section
        id="painting-demo"
        aria-labelledby="demo-title"
        className="border-y border-pine/10 bg-white/55"
      >
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-16 sm:px-8 lg:grid-cols-[.8fr_1.2fr] lg:py-24">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage">
              Interactive demonstration
            </p>
            <h2 id="demo-title" className="mt-3 font-cormorant text-4xl font-bold sm:text-5xl">
              Watch focus become a painting.
            </h2>
            <p className="mt-4 max-w-md leading-relaxed text-pine/65">
              This 30-second demonstration is only a preview. It never starts a
              focus session, changes tasks, earns rewards, or writes to your gallery.
            </p>

            <fieldset className="mt-7">
              <legend className="mb-3 text-sm font-bold">Choose a free scene</legend>
              <div className="flex flex-wrap gap-2">
                {FREE_SCENES.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={scene === item.id}
                    onClick={() => chooseScene(item.id)}
                    className={`rounded-full px-4 py-2 text-sm font-bold transition focus:outline-none focus:ring-2 focus:ring-sage ${
                      scene === item.id
                        ? 'bg-pine text-paper'
                        : 'border border-pine/15 bg-paper text-pine'
                    }`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </fieldset>
          </div>

          <div className="rounded-[2rem] border border-pine/10 bg-paper p-4 shadow-lg">
            <PaintingCanvas
              scene={scene}
              progress={progress}
              artSeed={92714}
              className="aspect-square w-full rounded-2xl object-contain"
            />
            <div className="mt-4">
              <div
                role="progressbar"
                aria-label="Painting demonstration progress"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={Math.round(progress * 100)}
                className="h-2 overflow-hidden rounded-full bg-pine/10"
              >
                <div
                  className="h-full rounded-full bg-sage transition-[width] motion-reduce:transition-none"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <span aria-live="polite" className="text-sm text-pine/60">
                  {demoState === 'complete'
                    ? 'Demonstration complete'
                    : `${Math.ceil((DEMO_DURATION - elapsedRef.current) / 1000)} seconds remaining`}
                </span>
                <div className="flex flex-wrap gap-2">
                  {demoState === 'ready' && (
                    <Button onClick={startDemo} icon={FiPlay}>Start demo</Button>
                  )}
                  {demoState === 'running' && (
                    <Button variant="secondary" onClick={pauseDemo} icon={FiPause}>
                      Pause
                    </Button>
                  )}
                  {demoState === 'paused' && (
                    <Button onClick={() => setDemoState('running')} icon={FiPlay}>
                      Resume
                    </Button>
                  )}
                  {demoState === 'complete' && (
                    <Button variant="secondary" onClick={replayDemo} icon={FiRefreshCw}>
                      Replay
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-20 sm:px-8">
        <div className="grid gap-5 md:grid-cols-3">
          {[
            ['01', 'Plan', 'Choose the meaningful work that deserves your attention.'],
            ['02', 'Focus', 'Work for a set time while your chosen scene develops.'],
            ['03', 'Keep', 'Complete the session to save the finished artwork in your gallery.'],
          ].map(([number, title, copy]) => (
            <article key={title} className="rounded-3xl border border-pine/10 bg-white p-7">
              <span className="text-xs font-bold tracking-[0.2em] text-sage">{number}</span>
              <h2 className="mt-6 font-cormorant text-3xl font-bold">{title}</h2>
              <p className="mt-2 leading-relaxed text-pine/60">{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-pine text-paper">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-16 sm:px-8 lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage">
              Free to begin
            </p>
            <h2 className="mt-3 font-cormorant text-4xl font-bold">
              The complete focus practice stays free.
            </h2>
            <p className="mt-3 max-w-2xl text-paper/70">
              Free includes unlimited focus sessions, planning, journal and habits,
              five Beyond the Garden artworks, saved paintings, standard export and
              backups. Studio Plus adds collections and high-resolution export.
            </p>
          </div>
          <div className="rounded-3xl bg-paper p-6 text-pine lg:min-w-72">
            <p className="text-sm font-bold">Studio Plus draft pricing</p>
            <p className="mt-2 font-cormorant text-3xl font-bold">
              {plus.prices.annual.label}
            </p>
            <p className="text-sm text-pine/60">
              USD billed annually, or {plus.prices.monthly.label}
            </p>
            <p className="mt-3 text-xs text-pine/55">
              {billingEnabled
                ? 'Purchase availability is provided by the connected billing service.'
                : 'Studio Plus purchases are not open yet.'}
            </p>
            <Link
              to="/membership"
              className="mt-5 inline-flex items-center gap-2 font-bold text-pine underline underline-offset-4"
            >
              View membership <SafeIcon icon={FiArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-pine/10 px-5 py-8 sm:px-8">
        <div className="mx-auto flex max-w-6xl flex-col justify-between gap-4 text-sm sm:flex-row">
          <p className="font-bold">MindGarden — Your effort, made visible.</p>
          <nav aria-label="Footer navigation" className="flex flex-wrap gap-5 text-pine/65">
            <Link to="/studio" className="hover:text-pine">Studio</Link>
            <Link to="/membership" className="hover:text-pine">Membership</Link>
            <Link to="/settings" className="hover:text-pine">Settings</Link>
          </nav>
        </div>
      </footer>
    </main>
  );
}